import React, { useState, useEffect } from 'react'
import fetchTeamSquads, { TeamSquad } from '../../api/fetchTeamSquads'
import styles from './matchSquads.module.css'
import { URL } from '../../utils/constants'

interface MatchSquadsProps {
	matchId: number
	homeTeam: string;
	awayTeam: string;
}

interface Player {
	number: number;
	photo: string;
	first_name: string;
	second_name: string;
	nick_name: string;
	citizenship: string;
	flag: string;
	type: string;
	role: string;
	from_start?: boolean;
	captain: boolean;
	team?: { name: string }[];
}

const MatchSquads: React.FC<MatchSquadsProps> = ({ matchId, homeTeam, awayTeam }) => {
	const [squadsData, setSquadsData] = useState<TeamSquad | null>(null)
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [error, setError] = useState<string | null>(null)
	const [displayedTeam, setDisplayedTeam] = useState<'home' | 'away'>('home');

	useEffect(() => {
		const loadSquadData = async () => {
			try {
				setIsLoading(true)
				const data = await fetchTeamSquads(matchId)
				setSquadsData(data)
				setError(null)
			} catch (err) {
				console.error('Failed to fetch squads data:', err)
				setError('Failed to load match squads. Please try again later.')
			} finally {
				setIsLoading(false)
			}
		}

		loadSquadData()
	}, [matchId])

	if (isLoading) return <div>Loading match squads...</div>
	if (error) return <div>Error: {error}</div>
	if (!squadsData) return <div>No squads data available.</div>

	const renderPlayer = (player: Player) => (
		<div className={styles.player} key={player.number}>
			<span className={styles.playerNumber}>{player.number}</span>
			<div className={styles.player_img__container}>
				{player.photo && (
					<img
						src={player.photo}
						alt={`${player.first_name} ${player.second_name}`}
					/>
				)}
			</div>
			<div className={styles.playerDetails}>
				{player.nick_name ? (
					<>{player.nick_name}</>
				) : (
					<>{`${player.first_name} ${player.second_name}`}</>
				)}
				<div className={styles.playerInfo}>
					<img src={`${URL}/${player.flag}`} className={styles.flag} alt={player.citizenship} />
					<span className={styles.playerRole}>{` ${player.role}`}</span>
					{player.captain && (
						<span className={styles.playerCaptain}>Captain</span>
					)}
				</div>
			</div>
		</div>
	)

	const renderTeam = (team: Player[]) => {
		const hasFromStartData = team.some(player => player.from_start !== undefined);
		const hasStartingPlayers = team.some(player => player.from_start === true);

		if (hasFromStartData && hasStartingPlayers) {
			const startingPlayers = team.filter(player => player.from_start === true);
			return (
				<div className={styles.team}>
					{startingPlayers.map(renderPlayer)}
				</div>
			);
		} else {
			return (
				<div className={styles.team}>
					{team.map(renderPlayer)}
				</div>
			);
		}
	}

	const renderSubstitutes = (team: Player[]) => {
		const hasFromStartData = team.some(player => player.from_start !== undefined);
		const hasStartingPlayers = team.some(player => player.from_start === true);

		if (hasFromStartData && hasStartingPlayers) {
			const substitutePlayers = team.filter(player => player.from_start === false);
			return substitutePlayers.length > 0 ? (
				<div className={styles.team}>
					{substitutePlayers.map(renderPlayer)}
				</div>
			) : null;
		}
		return null;
	}

	const shouldRenderSubstitutes = () => {
		return (
			squadsData.homeTeam.some(player => player.from_start !== undefined) &&
			squadsData.homeTeam.some(player => player.from_start === true) &&
			squadsData.awayTeam.some(player => player.from_start !== undefined) &&
			squadsData.awayTeam.some(player => player.from_start === true)
		);
	}

	const toggleTeam = () => {
		setDisplayedTeam(prevTeam => prevTeam === 'home' ? 'away' : 'home');
	};

	const homeTeamName = homeTeam || 'Home Team';
	const awayTeamName = awayTeam || 'Away Team';

	return (
		<div className={`${styles.matchSquadsContainer} ${displayedTeam === 'away' ? styles.showAwayTeam : ''}`}>
			<div className={styles.teamToggle}>
				<button onClick={toggleTeam} className={styles.toggleButton}>
          <span className={`${styles.teamName} ${displayedTeam === 'home' ? styles.activeTeam : ''}`}>
            {homeTeamName}
          </span>
					<span className={`${styles.teamName} ${displayedTeam === 'away' ? styles.activeTeam : ''}`}>
            {awayTeamName}
          </span>
				</button>
			</div>
			<div className={styles.teams__squads}>
				<div className={`${styles.HomeTeamArea} ${styles.mobileTeamArea}`}>
					{renderTeam(squadsData.homeTeam)}
				</div>
				<div className={styles.SpaceSquadsArea}>
					<div className={styles.SpaceAreaWithPlayers}></div>
				</div>
				<div className={`${styles.AwayTeamArea} ${styles.mobileTeamArea}`}>
					{renderTeam(squadsData.awayTeam)}
				</div>
			</div>
			{shouldRenderSubstitutes() && (
				<>
					<h3 className={styles.teams__squads_subheader}>Substitutes</h3>
					<div className={styles.teams__squads}>
						<div className={`${styles.HomeTeamArea} ${styles.mobileTeamArea}`}>
							{renderSubstitutes(squadsData.homeTeam)}
						</div>
						<div className={styles.SpaceSquadsArea}>
							<div className={styles.SpaceAreaWithPlayers}></div>
						</div>
						<div className={`${styles.AwayTeamArea} ${styles.mobileTeamArea}`}>
							{renderSubstitutes(squadsData.awayTeam)}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default MatchSquads;